/**
 * Feedback Modal Hook
 * 
 * React hook for managing the Report Issue modal state and context.
 * Provides easy integration across the app with automatic context detection.
 */

import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export interface FeedbackContext {
  runId?: string;
  currentPage?: string;
  previousPage?: string;
  additionalContext?: Record<string, any>;
}

export interface UseFeedbackModalReturn {
  /** Whether the modal is currently open */
  isOpen: boolean;
  /** Open the modal with optional context */
  openModal: (context?: Partial<FeedbackContext>) => void;
  /** Close the modal */
  closeModal: () => void;
  /** Current feedback context */
  context: FeedbackContext;
  /** Update context without opening modal */
  updateContext: (context: Partial<FeedbackContext>) => void;
}

/**
 * Hook for managing feedback modal state and context
 */
export function useFeedbackModal(): UseFeedbackModalReturn {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState<FeedbackContext>({});
  const [previousPage, setPreviousPage] = useState<string>();

  // Track page navigation for context
  useEffect(() => {
    setPreviousPage(context.currentPage);
    setContext(prev => ({
      ...prev,
      currentPage: location.pathname,
      previousPage: prev.currentPage
    }));
  }, [location.pathname]);

  /**
   * Open the feedback modal with optional context
   */
  const openModal = useCallback((newContext?: Partial<FeedbackContext>) => {
    if (newContext) {
      setContext(prev => ({
        ...prev,
        ...newContext,
        currentPage: newContext.currentPage || router.asPath
      }));
    }
    setIsOpen(true);
  }, [router.asPath]);

  /**
   * Close the feedback modal
   */
  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Update context without opening modal
   */
  const updateContext = useCallback((newContext: Partial<FeedbackContext>) => {
    setContext(prev => ({
      ...prev,
      ...newContext
    }));
  }, []);

  return {
    isOpen,
    openModal,
    closeModal,
    context,
    updateContext
  };
}

/**
 * Hook for quick feedback modal integration with automatic run ID detection
 */
export function useQuickFeedback(runId?: string) {
  const { openModal, ...rest } = useFeedbackModal();

  const reportIssue = useCallback((additionalContext?: Record<string, any>) => {
    openModal({
      runId,
      additionalContext
    });
  }, [openModal, runId]);

  return {
    reportIssue,
    ...rest
  };
}

export default useFeedbackModal;
